import {createContext, useContext, useEffect, useState} from "react";
import {onAuthStateChanged} from "@firebase/auth";
import {auth, edhrecFirestore} from "@/services/firebase.service";
import {collection, doc, getDoc} from "@firebase/firestore";
import Script from "next/script";

type AdContextType = boolean

const initialState: AdContextType = false

const Context = createContext(initialState as AdContextType)

export type PatreonObjectType = {
  currently_entitled_amount_cents: number
  full_name: string
  next_charge_date: string
  patron_status: string
}

const patronTier = (patreon: PatreonObjectType | undefined, isAdmin: boolean) => {
  if (isAdmin) return 'rare'

  if (patreon?.patron_status === 'active_patron') {
    if (patreon?.currently_entitled_amount_cents >= 500) return 'rare'
    else if (patreon?.currently_entitled_amount_cents >= 200) return 'uncommon'
    return undefined
  }

  return undefined
}

type Props = {
  children: React.ReactNode
}

const AdProvider = ({children}: Props) => {
  const [patreonTier, setPatreonTier] = useState<string | undefined | null>(null)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userCollection = collection(edhrecFirestore, 'users')
        const userDoc = doc(userCollection, user.uid)
        getDoc(userDoc).then((docSnapshot) => {
          const data = docSnapshot.data()
          if (data?.patreon) {
            const tier = patronTier(data.patreon as PatreonObjectType, !!data.isAdmin)
            setPatreonTier(tier)
          }
          else {
            setPatreonTier(undefined)
          }
        })
      }
      else {
        setPatreonTier(undefined)
      }
    })
    return () => unsub()
  }, [])

  const canRunAds = !(patreonTier === null || patreonTier)

  return (
    <Context.Provider value={canRunAds}>
      {canRunAds && <Script
        async
        data-cfasync='false'
        data-noptimize='1'
        src='//scripts.mediavine.com/tags/edhrec.js'
        type='text/javascript'
      />}
      {children}
    </Context.Provider>
  )
}

export const useCanRunAds = (): AdContextType => useContext(Context)

export default AdProvider